import { db, makeId } from "./db";
import type { ExerciseDefinition, Program, WorkoutTemplate, WorkoutTemplateExercise } from "./types";

function adaptPrescriptionForTracking(item: WorkoutTemplateExercise, replacement: ExerciseDefinition) {
  if (replacement.trackingMode === "duration") {
    const next = { ...item, minDuration: item.minDuration ?? 30, maxDuration: item.maxDuration ?? 60 };
    delete next.minReps;
    delete next.maxReps;
    return next;
  }
  const next = { ...item, minReps: item.minReps ?? 8, maxReps: item.maxReps ?? 12 };
  delete next.minDuration;
  delete next.maxDuration;
  return next;
}

export async function replaceTemplateExercise(templateId: string, templateExerciseId: string, replacementDefinitionId: string) {
  await db.transaction("rw", [db.templates, db.exercises], async () => {
    const [template, replacement] = await Promise.all([
      db.templates.get(templateId),
      db.exercises.get(replacementDefinitionId),
    ]);
    if (!template) throw new Error("Séance introuvable.");
    if (!replacement || replacement.archived) throw new Error("Exercice de remplacement introuvable.");
    const targetIndex = template.exercises.findIndex((item) => item.id === templateExerciseId);
    if (targetIndex < 0) throw new Error("Exercice à remplacer introuvable.");
    const exercises = structuredClone(template.exercises);
    exercises[targetIndex] = {
      ...adaptPrescriptionForTracking(exercises[targetIndex], replacement),
      exerciseDefinitionId: replacement.id,
    };
    await db.templates.update(templateId, { exercises, updatedAt: Date.now() });
  });
}

export async function createAndReplaceTemplateExercise(templateId: string, templateExerciseId: string, name: string) {
  const cleanName = name.trim();
  if (!cleanName) throw new Error("Le nouvel exercice doit avoir un nom.");
  let replacementId = "";
  await db.transaction("rw", [db.templates, db.exercises], async () => {
    const template = await db.templates.get(templateId);
    if (!template) throw new Error("Séance introuvable.");
    const target = template.exercises.find((item) => item.id === templateExerciseId);
    if (!target) throw new Error("Exercice à remplacer introuvable.");
    const currentDefinition = await db.exercises.get(target.exerciseDefinitionId);
    if (!currentDefinition) throw new Error("Exercice actuel introuvable.");
    const existing = await db.exercises.filter((item) => item.name.trim().toLocaleLowerCase("fr") === cleanName.toLocaleLowerCase("fr")).first();
    const replacement: ExerciseDefinition = existing ? { ...existing, archived: false, updatedAt: Date.now() } : {
      id: makeId("exercise"),
      name: cleanName,
      archived: false,
      isUnilateral: currentDefinition.isUnilateral,
      perSide: currentDefinition.perSide,
      trackingMode: currentDefinition.trackingMode,
      progressionMode: currentDefinition.progressionMode,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    await db.exercises.put(replacement);
    replacementId = replacement.id;
    const exercises = structuredClone(template.exercises);
    const targetIndex = exercises.findIndex((item) => item.id === templateExerciseId);
    exercises[targetIndex] = {
      ...adaptPrescriptionForTracking(exercises[targetIndex], replacement),
      exerciseDefinitionId: replacement.id,
    };
    await db.templates.update(templateId, { exercises, updatedAt: Date.now() });
  });
  return replacementId;
}

export async function createEmptyProgram(name: string): Promise<Program> {
  const cleanName = name.trim();
  if (!cleanName) throw new Error("Le programme doit avoir un nom.");
  const createdAt = Date.now();
  const program: Program = {
    id: makeId("program"),
    name: cleanName,
    templateIds: [],
    archived: false,
    isCurrent: true,
    createdAt,
    updatedAt: createdAt,
  };
  await db.transaction("rw", [db.programs, db.settings], async () => {
    await db.programs.toCollection().modify({ isCurrent: false });
    await db.programs.add(program);
    await db.settings.update("settings", { onboardingComplete: true });
  });
  return program;
}

export async function setCurrentProgram(programId: string) {
  await db.transaction("rw", db.programs, async () => {
    const target = await db.programs.get(programId);
    if (!target || target.archived) throw new Error("Programme introuvable ou archivé.");
    await db.programs.toCollection().modify({ isCurrent: false });
    await db.programs.update(programId, { isCurrent: true, updatedAt: Date.now() });
  });
}

export async function archiveProgram(programId: string) {
  await db.transaction("rw", db.programs, async () => {
    const target = await db.programs.get(programId);
    if (!target) return;
    await db.programs.update(programId, { archived: true, isCurrent: false, updatedAt: Date.now() });
    if (target.isCurrent) {
      const replacement = await db.programs.filter((item) => !item.archived && item.id !== programId).first();
      if (replacement) await db.programs.update(replacement.id, { isCurrent: true, updatedAt: Date.now() });
    }
  });
}

export async function restoreProgram(programId: string) {
  await db.transaction("rw", db.programs, async () => {
    const target = await db.programs.get(programId);
    if (!target) throw new Error("Programme introuvable.");
    const current = await db.programs.filter((item) => item.isCurrent && !item.archived).first();
    await db.programs.update(programId, { archived: false, isCurrent: !current || current.id === programId, updatedAt: Date.now() });
  });
}

/** Removing one occurrence must never disable shared exercises in other templates. */
export async function removeTemplateExercise(templateId: string, itemId: string) {
  await db.transaction("rw", db.templates, async () => {
    const template = await db.templates.get(templateId);
    if (!template) throw new Error("Séance introuvable.");
    const exercises = template.exercises.filter((item) => item.id !== itemId).map((item, position) => ({ ...item, position }));
    await db.templates.update(templateId, { exercises, updatedAt: Date.now() });
  });
}

export type PrescriptionField = "minSetCount" | "setCount" | "minReps" | "maxReps" | "minDuration" | "maxDuration" | "restSeconds" | "load";

export async function updateTemplatePrescription(templateId: string, itemId: string, key: PrescriptionField, value: number) {
  const isCount = key !== "load";
  const minimum = key === "restSeconds" || key === "load" ? 0 : 1;
  if (!Number.isFinite(value) || value < minimum || (isCount && !Number.isInteger(value)) || ((key === "setCount" || key === "minSetCount") && value > 20)) {
    throw new Error(key === "setCount" || key === "minSetCount" ? "Choisissez entre 1 et 20 séries." : "Saisissez une valeur valide, positive ou nulle pour la charge et le repos.");
  }
  await db.transaction("rw", db.templates, async () => {
    const template = await db.templates.get(templateId);
    if (!template) throw new Error("Séance introuvable.");
    const exercises = structuredClone(template.exercises);
    const item = exercises.find((entry) => entry.id === itemId);
    if (!item) throw new Error("Exercice introuvable.");
    item[key] = value;
    for (const [low, high] of [["minSetCount", "setCount"], ["minReps", "maxReps"], ["minDuration", "maxDuration"]] as const) {
      if (item[low] != null && item[high] != null && item[low]! > item[high]!) {
        if (key === low) item[high] = value;
        else item[low] = value;
      }
    }
    await db.templates.update(templateId, { exercises, updatedAt: Date.now() });
  });
}

export async function duplicateProgram(programId: string): Promise<Program> {
  const source = await db.programs.get(programId);
  if (!source) throw new Error("Programme introuvable.");
  const sourceTemplates = (await db.templates.bulkGet(source.templateIds)).filter(Boolean) as WorkoutTemplate[];
  const createdAt = Date.now();
  const newProgramId = makeId("program");
  const copies = sourceTemplates.map((template) => ({
    ...structuredClone(template),
    id: makeId("template"),
    programId: newProgramId,
    exercises: template.exercises.map((item) => ({ ...item, id: makeId("template_exercise") })),
    createdAt,
    updatedAt: createdAt,
  }));
  const program: Program = {
    id: newProgramId,
    name: `${source.name} — copie`,
    templateIds: copies.map((item) => item.id),
    archived: false,
    isCurrent: true,
    createdAt,
    updatedAt: createdAt,
  };
  await db.transaction("rw", [db.programs, db.templates], async () => {
    await db.programs.toCollection().modify({ isCurrent: false });
    if (copies.length) await db.templates.bulkAdd(copies);
    await db.programs.add(program);
  });
  return program;
}

export async function addWorkoutTemplate(programId: string, name: string): Promise<WorkoutTemplate> {
  const createdAt = Date.now();
  const template: WorkoutTemplate = {
    id: makeId("template"), programId, name: name.trim() || "Nouvelle séance",
    exercises: [], createdAt, updatedAt: createdAt,
  };
  await db.transaction("rw", [db.programs, db.templates], async () => {
    const program = await db.programs.get(programId);
    if (!program) throw new Error("Programme introuvable.");
    await db.templates.add(template);
    await db.programs.update(programId, { templateIds: [...program.templateIds, template.id], updatedAt: createdAt });
  });
  return template;
}
